import { useState, useEffect } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { 
  LayoutDashboard, Map, BookOpen, Settings, FileText, Calendar, 
  PanelLeftClose, PanelLeftOpen, Sparkles, Download, Search, LogIn, 
  User, LogOut, ChevronDown 
} from 'lucide-react';
import { SignedIn, SignedOut, UserButton, SignInButton, useUser, useClerk } from '@clerk/clerk-react';
import { cn } from '../lib/utils';
import { useStore } from '../store/useStore';
import { useTerminologia } from '../hooks/useTerminologia';
import { OnboardingScreen } from '../components/onboarding/OnboardingScreen';
import { CommandPalette } from '../components/CommandPalette';
import { ExportModal } from '../components/ExportModal';
import { subscribeTursoStatus, activateUserSync, deactivateUserSync, TursoSyncStatus } from '../db/tursoSync';
import { checkAndMigrateGuestData } from '../db/guestMigration';
import { GuestMigrationModal } from '../components/GuestMigrationModal';

export function MainLayout() {
  const { loadData, navigationTarget, clearNavigationTarget, asignaturas, isLoading, isSimulating, simulationChanges, resetSimulation, stopSimulation, isDemoMode, stopDemoMode } = useStore();
  const navigate = useNavigate();
  const { siglaFacultad, materias, facultadActiva } = useTerminologia();
  const { user, isSignedIn, isLoaded } = useUser();
  const { signOut } = useClerk();
  const [isExportOpen, setIsExportOpen] = useState(false);
  const [tursoStatus, setTursoStatus] = useState<TursoSyncStatus>('offline');
  const [pendingMigration, setPendingMigration] = useState<{ localCount: number; remoteCount: number } | null>(null);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState<boolean>(() => {
    return localStorage.getItem('fcea_sidebar_collapsed') === 'true';
  });

  useEffect(() => {
    return subscribeTursoStatus(setTursoStatus);
  }, []);

  // Sincronización dinámica con Turso + Migración de Datos de Invitado a Usuario
  useEffect(() => {
    async function handleAuthSync() {
      if (isSignedIn && user) {
        const meta = user.publicMetadata as Record<string, string>;
        const dbUrl = meta?.turso_db_url;
        const dbToken = meta?.turso_db_token;
        if (dbUrl && dbToken) {
          activateUserSync(dbUrl, dbToken);
        }

        // Evaluar migración de datos de invitado a la cuenta del usuario
        try {
          const migrationRes = await checkAndMigrateGuestData(user.id);
          if (migrationRes.type === 'merge_prompt_needed') {
            setPendingMigration({
              localCount: migrationRes.localCount || 0,
              remoteCount: migrationRes.remoteCount || 0,
            });
          } else if (migrationRes.migrated) {
            await loadData();
          }
        } catch (err) {
          console.warn('[MainLayout] Error evaluando migración de invitado:', err);
        }
      } else if (isSignedIn === false) {
        deactivateUserSync();
      }
    }

    handleAuthSync();
  }, [isSignedIn, user, loadData]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Observa el navigationTarget del store y navega a la vista correspondiente
  useEffect(() => {
    if (!navigationTarget) return;
    const routeMap: Record<string, string> = {
      dashboard: '/',
      roadmap: '/roadmap',
      asignaturas: '/asignaturas',
      horarios: '/horarios',
      notas: '/notas',
      config: '/config',
    };
    const route = routeMap[navigationTarget.view];
    if (route) navigate(route);
    // Limpiar el target DESPUÉS de navegar, en el siguiente tick
    // para que la vista destino pueda leer el asignaturaId
    setTimeout(() => clearNavigationTarget(), 100);
  }, [navigationTarget, navigate]);

  const toggleSidebar = () => {
    setIsCollapsed(prev => {
      const next = !prev;
      localStorage.setItem('fcea_sidebar_collapsed', String(next));
      return next;
    });
  };

  const links = [
    { to: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/roadmap', icon: Map, label: 'Roadmap' },
    { to: '/asignaturas', icon: BookOpen, label: materias },
    { to: '/horarios', icon: Calendar, label: 'Horarios' },
    { to: '/notas', icon: FileText, label: 'Notas' },
    { to: '/config', icon: Settings, label: 'Configuración' },
  ];

  const onboardingSkipped = localStorage.getItem('fcea_onboarding_skipped') === 'true';
  const showOnboarding = !isLoading && asignaturas.length === 0 && !onboardingSkipped && !isDemoMode && !isSignedIn;

  if (showOnboarding) {
    return <OnboardingScreen />;
  }

  const facultyIdAttr = facultadActiva?.id || 'fcea';

  return (
    <div data-faculty={facultyIdAttr} className="flex h-screen w-full bg-[#F9FAFB] overflow-hidden text-slate-900 font-sans selection:bg-primary/20 selection:text-primary relative">
      <CommandPalette />

      {pendingMigration && user && (
        <GuestMigrationModal
          userId={user.id}
          localCount={pendingMigration.localCount}
          remoteCount={pendingMigration.remoteCount}
          onResolved={() => setPendingMigration(null)}
        />
      )}

      {/* Floating Banner — Modo Demo (Sandbox Aislado) */}
      {isDemoMode && (
        <div className="fixed top-0 inset-x-0 z-[999] bg-slate-900 text-white px-4 py-2 flex items-center justify-between shadow-md border-b border-slate-700 animate-in slide-in-from-top duration-300">
          <div className="flex items-center gap-2 text-xs font-semibold">
            <Sparkles size={14} className="text-amber-400 shrink-0" />
            <span>Estás viendo datos de ejemplo (Sandbox Aislado)</span>
            <span className="hidden sm:inline text-slate-400 font-normal">| Tus datos reales están intactos.</span>
          </div>
          <button
            onClick={stopDemoMode}
            className="bg-white text-slate-900 hover:bg-slate-100 text-xs font-bold px-3 py-1 rounded-lg transition-all shadow-xs shrink-0 cursor-pointer"
          >
            Salir del recorrido de ejemplo
          </button>
        </div>
      )}

      {/* Floating Pill — Modo Simulación (glassmorphism, no empuja layout) */}
      {isSimulating && (
        <>
          <div className="fixed top-5 left-1/2 -translate-x-1/2 z-50 animate-in fade-in slide-in-from-top-2 duration-300 select-none">
            <div className="flex items-center gap-3 bg-white/90 backdrop-blur-md border border-primary/20 shadow-lg rounded-full px-5 py-2.5">
              <Sparkles size={15} className="text-primary shrink-0" />
              <span className="text-sm font-semibold text-primary tracking-tight whitespace-nowrap">
                Modo Simulación
              </span>
              {simulationChanges > 0 && (
                <span className="text-[11px] font-mono font-semibold text-primary bg-primary/10 border border-primary/20 px-2.5 py-0.5 rounded-full whitespace-nowrap">
                  {simulationChanges} {simulationChanges === 1 ? 'cambio' : 'cambios'}
                </span>
              )}
              <div className="w-px h-4 bg-slate-200/80" />
              <button
                onClick={resetSimulation}
                className="text-xs font-medium text-slate-500 hover:text-slate-800 transition-colors whitespace-nowrap"
              >
                Reiniciar
              </button>
              <button
                onClick={stopSimulation}
                className="text-xs font-medium text-slate-500 hover:text-rose-600 transition-colors whitespace-nowrap"
              >
                Salir
              </button>
            </div>
          </div>

          {/* Marca de Agua Rotada */}
          <div className="fixed inset-0 pointer-events-none flex items-center justify-center z-0 select-none overflow-hidden font-extrabold text-primary/[0.04] text-8xl md:text-9xl -rotate-45 tracking-widest">
            SIMULACIÓN
          </div>
        </>
      )}

      {/* Sidebar Premium — Antigravity x Notion/Linear */}
      <aside
        className={cn(
          "bg-white flex flex-col h-full border-r border-slate-200/70 transition-all duration-300 ease-in-out relative shrink-0 select-none shadow-[1px_0_4px_rgba(0,0,0,0.03)]",
          isCollapsed ? "w-[64px]" : "w-[236px]"
        )}
      >
        {/* User Profile Header Block & Ícono de Lupa Compacto */}
        <div className="relative border-b border-slate-100 p-2.5 flex items-center justify-between gap-1.5">
          <button
            type="button"
            onClick={() => setProfileMenuOpen(prev => !prev)}
            className={cn(
              "flex-1 flex items-center gap-2.5 p-1.5 rounded-xl hover:bg-slate-100/80 transition-all text-left group cursor-pointer select-none min-w-0",
              isCollapsed && "justify-center"
            )}
            title={isSignedIn ? (user?.fullName || user?.primaryEmailAddress?.emailAddress || 'Perfil de Usuario') : 'Modo Invitado'}
          >
            {/* Avatar / Inicial */}
            {isSignedIn && user?.imageUrl ? (
              <img
                src={user.imageUrl}
                alt="Avatar"
                className="w-8 h-8 rounded-full object-cover border border-slate-200 shrink-0"
              />
            ) : (
              <div className="w-8 h-8 rounded-full bg-primary text-white flex items-center justify-center font-bold text-xs shadow-2xs shrink-0 uppercase">
                {isSignedIn && user?.firstName ? user.firstName[0] : 'I'}
              </div>
            )}

            {!isCollapsed && (
              <div className="min-w-0 flex-1 truncate">
                <h2 className="text-xs font-bold text-slate-900 truncate leading-tight flex items-center gap-1">
                  <span className="truncate">
                    {isSignedIn ? (user?.fullName || user?.primaryEmailAddress?.emailAddress?.split('@')[0]) : 'Invitado'}
                  </span>
                  <ChevronDown size={12} className="text-slate-400 shrink-0 group-hover:text-slate-700 transition-colors" />
                </h2>
                <p className="text-[10px] font-mono font-semibold text-primary truncate leading-tight mt-0.5">
                  {siglaFacultad} • Udelar
                </p>
              </div>
            )}
          </button>

          {/* Ícono de Lupa Compacto para Búsqueda Global (⌘K / Ctrl+K) */}
          {!isCollapsed && (
            <button
              type="button"
              onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
              className="w-8 h-8 flex items-center justify-center rounded-xl bg-slate-100/80 hover:bg-primary/10 border border-slate-200/60 text-slate-500 hover:text-primary transition-all duration-200 group cursor-pointer shadow-2xs shrink-0"
              title="Buscar en todo... (⌘K / Ctrl+K)"
            >
              <Search className="w-4 h-4 text-slate-400 group-hover:text-primary transition-colors" />
            </button>
          )}

          {/* Menú Desplegable de Perfil / Sesión */}
          {profileMenuOpen && (
            <>
              <div
                className="fixed inset-0 z-40"
                onClick={() => setProfileMenuOpen(false)}
              />
              <div className="absolute top-full left-2 right-2 mt-1 z-50 bg-white rounded-xl border border-slate-200 shadow-lg p-1 space-y-0.5 text-xs animate-in fade-in duration-150">
                {isSignedIn ? (
                  <>
                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate('/config');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 text-left font-medium transition-colors cursor-pointer"
                    >
                      <User size={14} className="text-slate-400" />
                      <span>Ver perfil</span>
                    </button>

                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate('/config');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 text-left font-medium transition-colors cursor-pointer"
                    >
                      <Settings size={14} className="text-slate-400" />
                      <span>Configuración de cuenta</span>
                    </button>

                    <div className="my-1 border-t border-slate-100" />

                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        signOut();
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-rose-600 hover:bg-rose-50 text-left font-medium transition-colors cursor-pointer"
                    >
                      <LogOut size={14} className="text-rose-500" />
                      <span>Cerrar sesión</span>
                    </button>
                  </>
                ) : (
                  <>
                    <SignInButton mode="modal">
                      <button
                        onClick={() => setProfileMenuOpen(false)}
                        className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-primary hover:bg-primary/10 text-left font-semibold transition-colors cursor-pointer"
                      >
                        <LogIn size={14} className="text-primary" />
                        <span>Iniciar sesión / Registrarse</span>
                      </button>
                    </SignInButton>

                    <button
                      onClick={() => {
                        setProfileMenuOpen(false);
                        navigate('/config');
                      }}
                      className="w-full flex items-center gap-2 px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 text-left font-medium transition-colors cursor-pointer"
                    >
                      <Settings size={14} className="text-slate-400" />
                      <span>Configuración</span>
                    </button>
                  </>
                )}
              </div>
            </>
          )}
        </div>

        {/* Ícono de Lupa Compacto cuando el Sidebar está Colapsado */}
        {isCollapsed && (
          <div className="py-2 flex justify-center border-b border-slate-100/60">
            <button
              type="button"
              onClick={() => window.dispatchEvent(new KeyboardEvent('keydown', { key: 'k', ctrlKey: true }))}
              className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-100/80 hover:bg-primary/10 border border-slate-200/60 text-slate-500 hover:text-primary transition-all duration-200 group cursor-pointer shadow-2xs"
              title="Buscar en todo... (⌘K / Ctrl+K)"
            >
              <Search className="w-4 h-4 text-slate-400 group-hover:text-primary transition-colors" />
            </button>
          </div>
        )}

        {/* Navigation Links */}
        <nav className="flex-grow px-2 py-3 space-y-0.5">
          {links.map((link) => (
            <NavLink
              key={link.to}
              to={link.to}
              end={link.to === '/'}
              className={({ isActive }) => cn(
                "relative flex items-center gap-3 rounded-lg text-[13px] font-medium transition-all duration-200 group",
                isCollapsed ? "justify-center p-[10px] mx-auto w-10 h-10" : "px-3 py-[9px]",
                isActive
                  ? cn(
                      "text-primary bg-primary/10 font-semibold",
                      !isCollapsed && "border-l-[3px] border-primary pl-[9px] rounded-l-none"
                    )
                  : "text-slate-500 hover:bg-slate-100/50 hover:text-slate-800"
              )}
            >
              {({ isActive }) => (
                <>
                  <link.icon
                    className={cn(
                      "shrink-0 transition-colors",
                      isCollapsed ? "w-[18px] h-[18px]" : "w-4 h-4",
                      isActive ? "text-primary" : "text-slate-400 group-hover:text-slate-700"
                    )}
                  />
                  {!isCollapsed && (
                    <span className="truncate">{link.label}</span>
                  )}
                  {/* Tooltip elegante para modo colapsado */}
                  {isCollapsed && (
                    <div className="absolute left-full ml-2.5 px-2.5 py-1.5 bg-slate-800 text-white text-[11px] font-medium rounded-lg shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-all duration-150 z-50 whitespace-nowrap -translate-x-1 group-hover:translate-x-0">
                      {link.label}
                      {/* Caret */}
                      <span className="absolute -left-1 top-1/2 -translate-y-1/2 border-4 border-transparent border-r-slate-800" />
                    </div>
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        {/* Sección de Usuario & Autenticación Transparente de Clerk */}
        <div className={cn("px-2 py-2 border-t border-slate-100/80 flex flex-col justify-center select-none", isCollapsed ? "h-[56px]" : "h-[98px]")}>
          {!isLoaded ? (
            <div className={cn("w-full bg-slate-100/70 rounded-xl animate-pulse", isCollapsed ? "h-10 w-10 mx-auto" : "h-[82px]")} />
          ) : (
            <>
              <SignedIn>
                {!isCollapsed ? (
                  <div className="h-[82px] p-2 bg-primary/5 rounded-xl border border-primary/10 flex flex-col justify-between">
                    <div className="flex items-center gap-2">
                      <UserButton appearance={{ elements: { avatarBox: "w-7 h-7" } }} />
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold text-slate-800 truncate leading-tight">
                          {user?.primaryEmailAddress?.emailAddress || user?.fullName || 'Estudiante'}
                        </p>
                        <p className="text-[10px] text-emerald-600 font-medium truncate flex items-center gap-1 mt-0.5">
                          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 shrink-0" />
                          Sync Cuenta Activa
                        </p>
                      </div>
                    </div>
                    <button
                      onClick={() => signOut()}
                      className="text-[11px] font-medium text-slate-500 hover:text-rose-600 transition-colors text-left flex items-center gap-1 cursor-pointer pt-1 border-t border-primary/10"
                    >
                      <LogOut size={12} />
                      <span>Cerrar sesión</span>
                    </button>
                  </div>
                ) : (
                  <div className="w-10 h-10 mx-auto flex items-center justify-center">
                    <UserButton appearance={{ elements: { avatarBox: "w-8 h-8" } }} />
                  </div>
                )}
              </SignedIn>

              <SignedOut>
                {!isCollapsed ? (
                  <div className="h-[82px] p-2.5 bg-slate-50 border border-slate-200/60 rounded-xl flex flex-col justify-between">
                    <p className="text-[11px] text-slate-500 leading-tight">
                      <strong className="text-slate-700">Modo Local:</strong> Iniciá sesión para respaldar tu avance en la nube.
                    </p>
                    <SignInButton mode="modal">
                      <button className="w-full bg-primary hover:bg-primary-container text-white text-xs font-semibold py-1 px-3 rounded-lg flex items-center justify-center gap-1.5 transition-all shadow-xs cursor-pointer">
                        <LogIn size={12} />
                        Iniciar Sesión
                      </button>
                    </SignInButton>
                  </div>
                ) : (
                  <SignInButton mode="modal">
                    <button title="Iniciá sesión para sincronizar en la nube" className="w-10 h-10 mx-auto flex items-center justify-center bg-slate-100 hover:bg-primary/10 text-slate-600 hover:text-primary rounded-lg transition-colors cursor-pointer">
                      <LogIn size={16} />
                    </button>
                  </SignInButton>
                )}
              </SignedOut>
            </>
          )}
        </div>

        {/* Indicador Transparente de Sync Turso */}
        <div className="px-3 py-2 border-t border-slate-100/80">
          <div
            className={cn(
              "flex items-center gap-2 rounded-lg p-1.5 text-xs font-medium text-slate-500 bg-slate-50 border border-slate-100",
              isCollapsed && "justify-center p-2"
            )}
            title={`Estado de sincronización: ${tursoStatus}`}
          >
            <div className="relative flex items-center justify-center shrink-0">
              {tursoStatus === 'synced' && <span className="w-2 h-2 rounded-full bg-emerald-500 ring-2 ring-emerald-100" />}
              {tursoStatus === 'syncing' && <span className="w-2 h-2 rounded-full bg-blue-500 animate-ping" />}
              {tursoStatus === 'offline' && <span className="w-2 h-2 rounded-full bg-slate-400" />}
              {tursoStatus === 'disabled' && <span className="w-2 h-2 rounded-full bg-slate-300" />}
              {tursoStatus === 'error' && <span className="w-2 h-2 rounded-full bg-rose-500" />}
            </div>
            {!isCollapsed && (
              <span className="text-[11px] font-medium truncate text-slate-600">
                {tursoStatus === 'synced' && 'Sincronizado'}
                {tursoStatus === 'syncing' && 'Sincronizando...'}
                {tursoStatus === 'offline' && 'Sin conexión'}
                {tursoStatus === 'disabled' && 'Sync desactivado'}
                {tursoStatus === 'error' && 'Error sync'}
              </span>
            )}
          </div>
        </div>

        {/* Botón Export Data */}
        <div className="px-2 py-1.5 border-t border-slate-100/60">
          <button
            onClick={() => setIsExportOpen(true)}
            className={cn(
              "w-full flex items-center gap-3 rounded-lg text-[13px] font-medium text-slate-600 hover:bg-primary/10 hover:text-primary transition-all duration-200 group",
              isCollapsed ? "justify-center p-[10px] mx-auto w-10 h-10" : "px-3 py-[8px]"
            )}
            title="Exportar información académica"
          >
            <Download className="w-4 h-4 text-slate-400 group-hover:text-primary shrink-0" />
            {!isCollapsed && <span className="truncate">Exportar Datos</span>}
          </button>
        </div>

        {/* Botón de Colapsar — integrado en el footer */}
        <div className={cn(
          "border-t border-slate-100 flex items-center transition-all duration-300",
          isCollapsed ? "justify-center p-3" : "justify-between px-4 py-3"
        )}>
          {!isCollapsed && (
            <span className="text-[10px] font-mono text-slate-400 truncate">
              v0.1.0
            </span>
          )}
          <button
            onClick={toggleSidebar}
            title={isCollapsed ? "Expandir menú" : "Colapsar menú"}
            className="p-1.5 text-slate-400 hover:text-slate-700 hover:bg-slate-100 rounded-lg transition-all duration-200 shrink-0"
          >
            {isCollapsed ? <PanelLeftOpen size={16} /> : <PanelLeftClose size={16} />}
          </button>
        </div>
      </aside>

      {/* Modal de Exportación */}
      <ExportModal isOpen={isExportOpen} onClose={() => setIsExportOpen(false)} />

      {/* Main Workspace */}
      <main className="flex-1 flex flex-col min-w-0 overflow-hidden bg-[#F9FAFB] relative">
        <div className="flex-1 overflow-y-auto">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
